/**
 * @file        src/pages/site/SocialLinks.jsx
 * @description Edit Social Links (Site Management). Single-row upsert of the
 *              Facebook / Twitter / YouTube / Instagram / Google URLs stored on
 *              `site_settings`.
 * @author      Kinetika Labs
 * @created     2026-07-04
 * @changelog
 *   - 2026-07-04 Claude — Initial (Site Management → Social Links).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import { get, put } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';

const urlField = z.union([z.string().trim().url('Enter a valid URL'), z.literal('')]).optional();
const schema = z.object({
  social_facebook: urlField,
  social_twitter: urlField,
  social_youtube: urlField,
  social_instagram: urlField,
  social_google: urlField,
});

const FIELDS = [
  { key: 'social_facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/…' },
  { key: 'social_twitter', label: 'Twitter', placeholder: 'https://twitter.com/…' },
  { key: 'social_youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/…' },
  { key: 'social_instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/…' },
  { key: 'social_google', label: 'Google', placeholder: 'https://…' },
];

const empty = {
  social_facebook: '', social_twitter: '', social_youtube: '', social_instagram: '', social_google: '',
};

export default function SocialLinks() {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data } = useQuery({ queryKey: qk.siteSettings.get, queryFn: () => get('/site-settings') });

  useEffect(() => {
    const t = data?.data;
    if (t && Object.keys(t).length) {
      setForm({
        social_facebook: t.social_facebook || '', social_twitter: t.social_twitter || '',
        social_youtube: t.social_youtube || '', social_instagram: t.social_instagram || '',
        social_google: t.social_google || '',
      });
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: (body) => put('/site-settings', body),
    onSuccess: () => {
      toast.ok('Saved', 'Social links updated.');
      qc.invalidateQueries({ queryKey: qk.siteSettings.get });
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    mut.mutate(parsed.data);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead title="Edit Social Links" subtitle="Site Management" />
      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          {FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label>{f.label}</label>
              <input value={form[f.key]} onChange={on(f.key)} placeholder={f.placeholder} />
              {errors[f.key] ? <div className="field-error">{errors[f.key]}</div> : null}
            </div>
          ))}
          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}
